import { Customer } from '@/types/TCRMLead';
import { getPriorityColor, showNotification } from '@/utils/SmartCRM/crmDetailsUtils';
import { Button, Form, Select, Input } from 'antd';
import TextArea from 'antd/es/input/TextArea';
import { useState } from 'react';
import { FiUser, FiCalendar, FiEdit3, FiPlus, FiZap, FiClipboard } from 'react-icons/fi';
import { EFieldType } from 'src/enums/ESmartCRM';
import { useCRMLead } from 'src/Hooks/SmartCRM/useCRMLogs';

interface CustomerDetailsProps {
	customer: Customer;
	agentId: string;
}

const formatTimestamp = (timestamp: string | number) => {
	if (!timestamp) {
		return '';
	}

	try {
		const numericTimestamp = typeof timestamp === 'string' ? parseInt(timestamp, 10) : timestamp;

		if (numericTimestamp && numericTimestamp.toString().length === 13) {
			const date = new Date(numericTimestamp);
			return date.toLocaleString('en-US', {
				year: 'numeric',
				month: 'short',
				day: 'numeric',
				hour: '2-digit',
				minute: '2-digit',
				hour12: true,
			});
		}

		if (numericTimestamp && numericTimestamp.toString().length === 10) {
			const date = new Date(numericTimestamp * 1000);
			return date.toLocaleString('en-US', {
				year: 'numeric',
				month: 'short',
				day: 'numeric',
				hour: '2-digit',
				minute: '2-digit',
				hour12: true,
			});
		}

		return timestamp.toString();
	} catch {
		return timestamp?.toString() || '';
	}
};

const formatVariableValue = (variable) => {
	if (!variable.value && variable.value !== false && variable.value !== 0) {
		return 'No value';
	}

	const fieldType = variable.fieldType || variable.metadata?.fieldType;
	const fieldName = variable.fieldName || '';
	const isDateField =
		fieldType === EFieldType.DATE ||
		fieldName.toLowerCase().includes('date') ||
		fieldName.toLowerCase().includes('createdat') ||
		fieldName.toLowerCase().includes('updatedat') ||
		fieldName.toLowerCase().includes('time');

	switch (fieldType) {
		case EFieldType.BOOLEAN:
			return variable.value ? 'Yes' : 'No';
		case EFieldType.MULTISELECT:
			if (Array.isArray(variable.value)) {
				return variable.value.join(', ');
			}
			return variable.value;
		case EFieldType.DATE:
			return formatTimestamp(variable.value);
		case EFieldType.SELECT:
		case EFieldType.TEXT:
		case EFieldType.NUMBER:
		default:
			if (isDateField) {
				return formatTimestamp(variable.value);
			}
			return variable.value;
	}
};

export const CustomerDetails: React.FC<CustomerDetailsProps> = ({ customer, agentId }) => {
	const [isAddingNote, setIsAddingNote] = useState(false);
	const [form] = Form.useForm();

	const {
		lead,
		notes,
		loading: notesLoading,
		error: notesError,
		addNote,
	} = useCRMLead(customer.id, agentId);

	const currentCustomer = lead || customer;

	const handleAddNote = () => {
		setIsAddingNote(true);
	};

	const handleSaveNote = async (values: any) => {
		const noteData = {
			name: values.name,
			note: values.note,
			priority: values.priority,
			followUpDate: values.followUpDate ? values.followUpDate.format('YYYY-MM-DD') : undefined,
		};

		try {
			await addNote(noteData);
			setIsAddingNote(false);
			form.resetFields();
			showNotification('success', 'Note Added', 'Your note has been saved successfully');
		} catch (error: any) {
			showNotification('error', 'Failed to Add Note', error.message || 'Something went wrong');
		}
	};

	const handleCancelNote = () => {
		setIsAddingNote(false);
		form.resetFields();
	};

	const formatDate = (dateString?: string) => {
		if (!dateString) {
			return 'N/A';
		}

		const date = new Date(dateString);
		if (isNaN(date.getTime())) {
			return 'Invalid Date';
		}

		return date.toLocaleDateString('en-US', {
			year: 'numeric',
			month: 'short',
			day: 'numeric',
			hour: '2-digit',
			minute: '2-digit',
		});
	};

	return (
		<div className="space-y-6 p-4">
			<div className="bg-white rounded-lg border p-6">
				<div className="flex items-center gap-2 mb-4">
					<FiUser className="w-5 h-5 text-blue-500" />
					<h3 className="text-lg font-semibold">Contact Information</h3>
				</div>
				<div className="grid grid-cols-2 gap-6">
					<div className="space-y-4">
						<div>
							<label className="text-sm font-medium text-gray-500">Name</label>
							<p className="text-sm text-gray-900">{currentCustomer.name || 'N/A'}</p>
						</div>
						<div>
							<label className="text-sm font-medium text-gray-500">Phone Number</label>
							<p className="text-sm text-gray-900">{currentCustomer.number || 'N/A'}</p>
						</div>
						<div>
							<label className="text-sm font-medium text-gray-500">Email</label>
							<p className="text-sm text-gray-900">{currentCustomer.email || 'N/A'}</p>
						</div>
					</div>
					<div className="space-y-4">
						<div>
							<label className="text-sm font-medium text-gray-500">Created At</label>
							<p className="text-sm text-gray-900">
								{currentCustomer.createdAt ? formatDate(currentCustomer.createdAt) : 'N/A'}
							</p>
						</div>
						<div>
							<label className="text-sm font-medium text-gray-500">Last Updated</label>
							<p className="text-sm text-gray-900">
								{currentCustomer.updatedAt ? formatDate(currentCustomer.updatedAt) : 'N/A'}
							</p>
						</div>
					</div>
				</div>
			</div>

			{currentCustomer.variables && currentCustomer.variables.length > 0 && (
				<div className="bg-white rounded-lg border p-6">
					<div className="flex items-center gap-2 mb-4">
						<FiZap className="w-5 h-5 text-purple-500" />
						<h3 className="text-lg font-semibold">Smart Variables</h3>
					</div>
					<p className="text-sm text-gray-600 mb-4">
						Customer information collected during interactions
					</p>
					<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
						{currentCustomer.variables.map((variable, index) => (
							<div
								key={variable.id || index}
								className="bg-gray-50 p-4 rounded-lg border border-gray-200 hover:bg-gray-100 transition-colors"
							>
								<div className="text-sm font-medium text-gray-600 mb-2">{variable.fieldName}</div>
								<div className="text-sm text-gray-900 font-medium">
									{formatVariableValue(variable)}
								</div>
								{variable.metadata?.fieldType && (
									<div className="text-xs text-gray-500 mt-1">
										Type: {variable.metadata.fieldType}
									</div>
								)}
							</div>
						))}
					</div>
				</div>
			)}

			<div className="bg-white rounded-lg border p-6">
				<div className="flex items-center justify-between mb-4">
					<div className="flex items-center gap-2">
						<FiClipboard className="w-5 h-5 text-green-500" />
						<h3 className="text-lg font-semibold">Notes & Follow-ups</h3>
					</div>
					<Button
						type="primary"
						icon={<FiPlus />}
						onClick={handleAddNote}
						disabled={isAddingNote || notesLoading}
						loading={notesLoading}
					>
						Add Note
					</Button>
				</div>

				{isAddingNote && (
					<div className="mb-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
						<Form form={form} onFinish={handleSaveNote} layout="vertical">
							<Form.Item
								name="name"
								label="Name"
								rules={[{ required: true, message: 'Please enter your name' }]}
							>
								<Input placeholder="Enter your name..." className="w-full" />
							</Form.Item>

							<Form.Item
								name="note"
								label="Note"
								rules={[{ required: true, message: 'Please enter a note' }]}
							>
								<TextArea rows={3} placeholder="Enter your note here..." className="resize-none" />
							</Form.Item>

							<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
								<Form.Item
									name="priority"
									label="Priority"
									rules={[{ required: true, message: 'Please select priority' }]}
									initialValue="Medium"
								>
									<Select>
										<Select.Option value="High">High Priority</Select.Option>
										<Select.Option value="Medium">Medium Priority</Select.Option>
										<Select.Option value="Low">Low Priority</Select.Option>
									</Select>
								</Form.Item>
							</div>

							<div className="flex gap-2">
								<Button type="primary" htmlType="submit" loading={notesLoading}>
									Save Note
								</Button>
								<Button onClick={handleCancelNote} disabled={notesLoading}>
									Cancel
								</Button>
							</div>
						</Form>
					</div>
				)}

				<div className="space-y-4">
					{notesError && (
						<div className="text-center py-4 text-red-500">
							<p>Error loading notes: {notesError}</p>
						</div>
					)}

					{notes.length === 0 && !notesLoading ? (
						<div className="text-center py-8 text-gray-500">
							<FiEdit3 className="w-12 h-12 mx-auto mb-3 text-gray-300" />
							<p>No notes yet. Add your first note to get started.</p>
						</div>
					) : (
						notes.map((note, index) => (
							<div
								key={note.id || index}
								className="border border-gray-200 rounded-lg p-4 hover:shadow-sm transition-shadow"
							>
								<div className="flex items-start justify-between mb-3">
									<div className="flex items-center gap-3">
										<div className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center flex-shrink-0">
											<FiUser className="w-4 h-4 text-gray-600" />
										</div>
										<div>
											<span className="font-medium text-gray-900">{note.name}</span>
											<p className="text-sm text-gray-500">{note.date}</p>
										</div>
									</div>
									<div className="flex flex-col sm:flex-row items-end sm:items-center gap-2">
										<span
											className={`px-2 py-1 rounded text-xs font-medium ${getPriorityColor(note.priority)}`}
										>
											{note.priority} Priority
										</span>
										{note.followUpDate && (
											<span className="px-2 py-1 rounded text-xs font-medium bg-yellow-100 text-yellow-800 flex items-center gap-1">
												<FiCalendar className="w-3 h-3" />
												Follow-up: {note.followUpDate}
											</span>
										)}
									</div>
								</div>
								<p className="text-gray-700 leading-relaxed ml-11">{note.note}</p>
							</div>
						))
					)}

					{notesLoading && notes.length === 0 && (
						<div className="text-center py-8 text-gray-500">
							<div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-3" />
							<p>Loading notes...</p>
						</div>
					)}
				</div>
			</div>
		</div>
	);
};
